import { ItemZoneContextType } from '@context/ItemZoneContext'
import { BackMaterialInfo } from '@types'
import getStandardMaterial from '@utils/conversion/getStandardMaterial'
import { ProjectActions } from '../../../store/projectActions'
import { useAppStore } from '../../../store'

export default function handleTextureClick(
  ctx: ItemZoneContextType,
  texture: BackMaterialInfo,
  editZone: ProjectActions['editZone'],
) {
  // Obtener el objeto directamente del store para comparar con la textura REAL del store
  // No usar ctx.currentTexture porque se actualiza antes de aplicar el cambio
  const store = useAppStore.getState()
  const currentObjectID = store.currentObjectID

  if (!currentObjectID) {
    return
  }

  // Intentar obtener el objeto desde object.current o directamente del store
  const sceneObject =
    ctx.object.current || store.items.get(currentObjectID) || store.areas.get(currentObjectID)

  if (!sceneObject?.id) {
    return
  }

  // Preservar zoneName y zoneDisplayName ANTES de actualizar currentTexture
  const zoneName = ctx.currentTexture?.zoneName
  const zoneDisplayName = ctx.currentTexture?.zoneDisplayName

  if (!zoneName) {
    return
  }

  // Comparar con la textura REAL del store, no con currentTexture que puede estar desactualizado
  const currentTextureInStore = sceneObject.textures.find((t: any) => t.zoneName === zoneName)
  const isSameTexture = texture.idTexturas === currentTextureInStore?.searchID

  if (isSameTexture) {
    return
  }

  // Obtener acabado y modelo del textureInfo del item (vienen del backend)
  // Estos valores deben preservarse cuando se aplica una nueva textura
  // IMPORTANTE: Si acabado/modelo son null, mantenerlos como null (no usar zoneDisplayName)
  // porque zoneDisplayName viene de "descripcion" y no es lo mismo que acabado/modelo
  const textureInfo = sceneObject.textures.find((t: any) => t.zoneName === zoneName)
  const acabadoToUse = textureInfo?.acabado ?? ctx.currentTexture?.acabado ?? null
  const modeloToUse = textureInfo?.modelo ?? ctx.currentTexture?.modelo ?? null

  // IMPORTANTE: Pasar acabado y modelo a getStandardMaterial para preservarlos
  const newTexture = getStandardMaterial(texture, acabadoToUse, modeloToUse)

  // Asegurar que la URL sea completa y no tenga parámetros de query
  const cleanUrl = newTexture.url?.split('?')[0] || newTexture.url
  const finalTexture = {
    ...newTexture,
    url: cleanUrl, // Guardar URL limpia sin parámetros de query
    // Asegurar que acabado y modelo estén presentes
    acabado: newTexture.acabado || acabadoToUse,
    modelo: newTexture.modelo || modeloToUse,
    // Asegurar que displacementScale e img estén presentes
    displacementScale: newTexture.displacementScale ?? 0.2,
    img: newTexture.img || null,
  }

  // Actualizar currentTexture ANTES de aplicar la textura para evitar el retraso de un click
  // Preservar zoneName y zoneDisplayName del currentTexture actual
  ctx.setCurrentTexture((el) => ({
    ...el,
    ...finalTexture,
    zoneName: el?.zoneName || zoneName, // Preservar zoneName
    zoneDisplayName: el?.zoneDisplayName || zoneDisplayName, // Preservar zoneDisplayName
  }))

  // Aplicar la textura al item seleccionado
  editZone(sceneObject.id, zoneName, finalTexture)

  // Si es un item universal, aplicar la misma textura a todos los otros items universales del mismo tipo
  if (sceneObject.type === 'items' && (sceneObject as any).universal === true) {
    const currentItem = sceneObject as any
    const allItems = store.items

    // Buscar todos los items universales del mismo tipo (mismo idUnidades o glbPath)
    const otherUniversalItems = Array.from(allItems.values()).filter((item: any) => {
      return (
        item.type === 'items' &&
        item.universal === true &&
        item.id !== currentItem.id &&
        (item.idUnidades === currentItem.idUnidades || item.glbPath === currentItem.glbPath)
      )
    })

    // Aplicar la misma textura a todos los otros items universales
    otherUniversalItems.forEach((otherItem: any) => {
      // Verificar que el otro item tenga la misma zona
      const otherItemZone = otherItem.textures?.find((t: any) => t.zoneName === zoneName)
      if (otherItemZone) {
        // Aplicar la textura con un pequeño delay para evitar problemas de sincronización
        setTimeout(() => {
          editZone(otherItem.id, zoneName, finalTexture)
        }, 10)
      }
    })
  }
}
